import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import InfoPage from ".";
import {
  SummitApplicationStyles,
  SUMMIT_META_KEYS,
  getMetaEl,
} from "./SummitApplication";

// Postaway Hospitality Summit 2027 — early access ticket page.
// Mirrors the 2026 page (SummitApplication.js) and shares its .summit-* styles;
// 2027-specific additions use the .s27-* prefix below.

const EVENT_YEAR = "2027";
const PAGE_PATH = "/hospitality-summit-2027";
const SUMMIT_TITLE = "Postaway - Hospitality Summit 2027";
// Fixed refund deadline for every ticket. The same date lives in
// api/_lib/summitTicket.mjs (REFUND_DEADLINE_LABEL) and api/og/hospitality-summit.mjs.
const REFUND_DEADLINE_LABEL = "November 30, 2026";
const SUMMIT_DESCRIPTION = `Postaway - Hospitality Summit 2027 — September 24–26, 2027 · Breckenridge, Colorado. Early access tickets are $399 (regularly $699) and fully refundable until ${REFUND_DEADLINE_LABEL}.`;

const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  "pk_live_51SDWk7GLCy9PVleCNU1Eow6f4yVZDhCTbLH0vNdUKbBqk05OsixJ2o17BfOwgXv5G69VsQe64z13tpB0nCtkwGrF00w3Ju6FYI";

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
};

const REGULAR_PRICE_LABEL = "$699";
const EARLY_BIRD_PRICE_LABEL = "$399";
const EARLY_BIRD_SAVINGS_LABEL = "$300";
const EVENT_DATES = "September 24–26, 2027";
const EVENT_LOCATION = "Breckenridge, Colorado";
const REFUND_NOTE = `Fully refundable until ${REFUND_DEADLINE_LABEL}`;
const SPEAKER_TOPIC_MAX = 500;

const formatUsd = (cents) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

const TICKETS = [
  { id: "creator", label: "Creator Ticket", description: "Good for all 3 days" },
  { id: "host", label: "Host Ticket", description: "Good for all 3 days" },
];

// Third route on the ticket picker: speakers and sponsors register interest
// without buying a ticket (speakers are usually comped; a sponsor is already
// waiting to submit). Posts to /api/summit/interest — no Stripe involved.
const INTEREST_ID = "interest";
const INTEREST_TYPES = [
  { id: "speaker", label: "Speaking" },
  { id: "sponsor", label: "Sponsoring" },
  { id: "both", label: "Both" },
];
const TOPIC_MAX = 1000;
const topicLabelFor = (interestType) =>
  interestType === "sponsor"
    ? "What would you like to sponsor or offer?"
    : interestType === "both"
      ? "Your topic and what you'd like to offer"
      : "What would you like to speak about?";
const topicPlaceholderFor = (interestType) =>
  interestType === "sponsor"
    ? "Tell us about your company and what you have in mind — products, a session, a happy hour…"
    : "A topic, a story, or an area of expertise you'd love to share…";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const HIGHLIGHTS = [
  {
    label: "Dedicated Tracks",
    text: "Hosts and creators each get sessions built specifically for their side of the hospitality industry.",
  },
  {
    label: "Combined Sessions",
    text: "Hosts + creators come together to learn, connect, and explore the topics that benefit both sides.",
  },
  {
    label: "Three Days in Breckenridge",
    text: "A casual, connection-first event in the Rockies. Pack your comfortable flannels — there are no lanyards here.",
  },
];

const scrollToTickets = (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById("tickets");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Hero shortcut for speakers/sponsors: pre-select the interest route, then
// scroll. The tickets component listens for this event.
const SELECT_INTEREST_EVENT = "s27:select-interest";
const jumpToInterest = (e) => {
  if (e) e.preventDefault();
  window.dispatchEvent(new CustomEvent(SELECT_INTEREST_EVENT));
  scrollToTickets();
};

const EarlyBirdPrice = ({ className = "", size = "default" }) => (
  <span className={`s27-price s27-price-${size} ${className}`.trim()}>
    <s className="s27-price-was" aria-label={`Regular price ${REGULAR_PRICE_LABEL}`}>
      {REGULAR_PRICE_LABEL}
    </s>
    <span className="s27-price-now">{EARLY_BIRD_PRICE_LABEL}</span>
  </span>
);

const Summit2027Tickets = () => {
  const hasReturnSession =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("session_id");

  const [phase, setPhase] = useState(hasReturnSession ? "verifying" : "select");
  const [ticketType, setTicketType] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    propertyName: "",
    socialHandle: "",
    couponCode: "",
    speakerInterest: false,
    speakerTopic: "",
    interestType: "speaker",
    topic: "",
  });
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [applied, setApplied] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [notice, setNotice] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const sectionRef = useRef(null);

  const isHost = ticketType === "host";
  const isInterest = ticketType === INTEREST_ID;

  useEffect(() => {
    const onSelectInterest = () => {
      setTicketType(INTEREST_ID);
      setTouched((prev) => ({ ...prev, ticketType: true }));
      setPhase((p) => (p === "interestSent" || p === "select" ? "select" : p));
    };
    window.addEventListener(SELECT_INTEREST_EVENT, onSelectInterest);
    if (typeof window !== "undefined" && window.location.hash === "#speak") {
      onSelectInterest();
    }
    return () => window.removeEventListener(SELECT_INTEREST_EVENT, onSelectInterest);
  }, []);

  const errors = useMemo(() => {
    const next = {};
    if (!ticketType) next.ticketType = "Choose your ticket to continue.";
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_REGEX.test(form.email.trim()))
      next.email = "That email doesn't look right.";
    if (isInterest) {
      if (!form.topic.trim())
        next.topic =
          form.interestType === "sponsor"
            ? "Tell us a little about what you'd like to sponsor."
            : "Tell us a little about your topic.";
    } else if (isHost && !form.propertyName.trim()) {
      next.propertyName = "Please enter your property name.";
    }
    return next;
  }, [ticketType, form, isHost, isInterest]);

  const showError = (field) =>
    (touched[field] || submitAttempted) && errors[field] ? errors[field] : "";

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const markTouched = (field) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  useEffect(() => {
    if (!hasReturnSession) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cleanUrl = () =>
      window.history.replaceState({}, "", window.location.pathname);

    fetch(`/api/summit/checkout?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => r.json())
      .then((data) => {
        cleanUrl();
        if (data?.ok && data.paid) {
          setConfirmation(data);
          setPhase("confirmed");
        } else {
          setNotice(
            "Your payment wasn't completed, so no charge was made. Feel free to try again below."
          );
          setPhase("select");
        }
      })
      .catch(() => {
        cleanUrl();
        setNotice(
          "We couldn't verify your payment just now. If you completed checkout, your confirmation email is on its way — otherwise, try again below."
        );
        setPhase("select");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      phase === "verifying" ||
      phase === "confirmed" ||
      phase === "checkout" ||
      phase === "interestSent"
    ) {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [phase]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitAttempted(true);
      setSubmitError("");
      if (Object.keys(errors).length) return;

      setSubmitting(true);

      if (isInterest) {
        try {
          const res = await fetch("/api/summit/interest", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              event: EVENT_YEAR,
              interestType: form.interestType,
              name: form.name.trim(),
              email: form.email.trim(),
              socialHandle: form.socialHandle.trim(),
              topic: form.topic.trim(),
            }),
          });
          const data = await res.json();
          if (data?.ok) {
            setNotice("");
            setPhase("interestSent");
          } else {
            setSubmitError("Something went wrong — please try again.");
          }
        } catch (err) {
          setSubmitError("Something went wrong — please try again.");
        } finally {
          setSubmitting(false);
        }
        return;
      }

      try {
        const res = await fetch("/api/summit/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event: EVENT_YEAR,
            ticketType,
            name: form.name.trim(),
            email: form.email.trim(),
            propertyName: form.propertyName.trim(),
            socialHandle: form.socialHandle.trim(),
            couponCode: form.couponCode.trim(),
            speakerInterest: form.speakerInterest,
            speakerTopic: form.speakerInterest ? form.speakerTopic.trim() : "",
          }),
        });
        const data = await res.json();
        if (data?.ok && data.clientSecret) {
          setNotice("");
          setCouponError("");
          setApplied(
            data.couponCode
              ? { amountTotal: data.amountTotal, couponCode: data.couponCode }
              : null
          );
          setClientSecret(data.clientSecret);
          setPhase("checkout");
        } else if (data?.error === "invalid_coupon") {
          setCouponOpen(true);
          setCouponError(
            "That coupon code isn't valid for 2027 summit tickets. Check the code and try again, or clear it to continue."
          );
        } else {
          setSubmitError("Something went wrong — please try again.");
        }
      } catch (err) {
        setSubmitError("Something went wrong — please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [errors, ticketType, form, isInterest]
  );

  const handleEditDetails = () => {
    setClientSecret("");
    setPhase("select");
  };

  const backToTickets = (e) => {
    if (e) e.preventDefault();
    setTicketType("");
    setSubmitAttempted(false);
    setTouched({});
    setPhase("select");
  };

  const selectedTicket = TICKETS.find((t) => t.id === ticketType);
  const interestLabel =
    INTEREST_TYPES.find((t) => t.id === form.interestType)?.label || "Speaking";

  return (
    <div className="summit-tickets" id="tickets" ref={sectionRef}>
      <div className="summit-tickets-eyebrow">
        {isInterest && phase === "select"
          ? "Speak or Sponsor in 2027"
          : "Get Your Early Access Ticket"}
      </div>

      {phase === "interestSent" && (
        <div className="summit-confirmed s27-interest-sent" role="status">
          <div className="summit-confirmed-title">Thanks — we've got it!</div>
          <div className="summit-confirmed-ticket">
            {interestLabel} · {EVENT_DATES}
          </div>
          <p className="summit-confirmed-note">
            Jamie reads every one of these and will be in touch
            {form.email.trim() ? (
              <>
                {" "}
                at <strong>{form.email.trim()}</strong>
              </>
            ) : null}{" "}
            as the 2027 lineup comes together. No ticket was purchased and
            nothing was charged.
          </p>
          <button
            type="button"
            className="summit-btn primary s27-interest-ticket-cta"
            onClick={backToTickets}
          >
            Want to attend too? Get an early access ticket
          </button>
        </div>
      )}

      {phase === "verifying" && (
        <div className="summit-verifying">
          <div className="summit-spinner" aria-hidden="true" />
          <div className="summit-verifying-text">Confirming your ticket…</div>
        </div>
      )}

      {phase === "confirmed" && (
        <div className="summit-confirmed" role="status">
          <div className="summit-confirmed-title">You're in!</div>
          <div className="summit-confirmed-ticket">
            {confirmation?.ticketType === "host"
              ? "Host Ticket (Good for all 3 days)"
              : "Creator Ticket (Good for all 3 days)"}{" "}
            · Early Access
          </div>
          <div className="summit-confirmed-meta">
            {EVENT_DATES} · {EVENT_LOCATION}
          </div>
          <p className="summit-confirmed-note">
            A confirmation email is on its way
            {confirmation?.email ? (
              <>
                {" "}
                to <strong>{confirmation.email}</strong>
              </>
            ) : null}
            . Your ticket is {REFUND_NOTE.charAt(0).toLowerCase() + REFUND_NOTE.slice(1)}.
            {confirmation?.speakerInterest
              ? " Thanks for raising your hand to speak — we'll be in touch as we build the 2027 lineup."
              : ""}{" "}
            We can't wait to welcome you back to Breckenridge!
          </p>
        </div>
      )}

      {phase === "checkout" && (
        <div className="summit-checkout">
          <div className="summit-checkout-summary">
            <span className="summit-checkout-summary-text">
              {selectedTicket ? selectedTicket.label : "Summit Ticket"} × 1 ·{" "}
              {applied ? (
                <>
                  <s className="summit-checkout-strike">{EARLY_BIRD_PRICE_LABEL}</s>{" "}
                  {formatUsd(applied.amountTotal)} with code {applied.couponCode}
                </>
              ) : (
                <>
                  <s className="summit-checkout-strike">{REGULAR_PRICE_LABEL}</s>{" "}
                  {EARLY_BIRD_PRICE_LABEL} Early Access
                </>
              )}
              {form.email.trim() ? ` · ${form.email.trim()}` : ""}
            </span>
            <button
              type="button"
              className="summit-checkout-edit"
              onClick={handleEditDetails}
            >
              Edit details
            </button>
          </div>
          <div className="summit-checkout-frame">
            <EmbeddedCheckoutProvider
              key={clientSecret}
              stripe={getStripe()}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
          <div className="s27-refund-note">
            <span aria-hidden="true">↩︎</span> {REFUND_NOTE}.
          </div>
        </div>
      )}

      {phase === "select" && (
        <form className="summit-ticket-form" onSubmit={handleSubmit} noValidate>
          {notice && <div className="summit-notice">{notice}</div>}

          <div
            className="summit-ticket-cards s27-ticket-cards"
            role="radiogroup"
            aria-label="Ticket type"
          >
            {TICKETS.map((ticket) => (
              <label
                key={ticket.id}
                className={`summit-ticket-card s27-ticket-card${
                  ticketType === ticket.id ? " selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="ticketType"
                  value={ticket.id}
                  checked={ticketType === ticket.id}
                  onChange={() => {
                    setTicketType(ticket.id);
                    setTouched((prev) => ({ ...prev, ticketType: true }));
                  }}
                />
                <span className="summit-ticket-card-check" aria-hidden="true">
                  ✓
                </span>
                <span className="s27-ticket-card-tag">Early Access</span>
                <span className="summit-ticket-card-label">{ticket.label}</span>
                <EarlyBirdPrice className="summit-ticket-card-price" size="card" />
                <span className="summit-ticket-card-desc">
                  {ticket.description}
                </span>
              </label>
            ))}
            <label
              className={`summit-ticket-card s27-ticket-card s27-interest-card${
                isInterest ? " selected" : ""
              }`}
            >
              <input
                type="radio"
                name="ticketType"
                value={INTEREST_ID}
                checked={isInterest}
                onChange={() => {
                  setTicketType(INTEREST_ID);
                  setTouched((prev) => ({ ...prev, ticketType: true }));
                }}
              />
              <span className="summit-ticket-card-check" aria-hidden="true">
                ✓
              </span>
              <span className="s27-ticket-card-tag s27-ticket-card-tag-interest">
                No ticket needed
              </span>
              <span className="summit-ticket-card-label">Speaker or Sponsor</span>
              <span className="s27-interest-card-price">Interest form</span>
              <span className="summit-ticket-card-desc">
                Tell us you'd like to speak or sponsor — free
              </span>
            </label>
          </div>
          {showError("ticketType") && (
            <div className="summit-field-error summit-ticket-cards-error">
              {errors.ticketType}
            </div>
          )}
          {isInterest ? (
            <p className="summit-ticket-note">
              Interested in speaking or sponsoring? Tell us below — no ticket
              required. Jamie will reach out personally.
            </p>
          ) : (
            <p className="summit-ticket-note">
              If you are both a host and creator, simply choose the category that
              most interests you.
            </p>
          )}

          {isInterest && (
            <div className="summit-field">
              <span className="summit-field-label" id="summit-interest-type-label">
                I'm interested in
              </span>
              <div
                className="s27-interest-chips"
                role="radiogroup"
                aria-labelledby="summit-interest-type-label"
              >
                {INTEREST_TYPES.map((t) => (
                  <label
                    key={t.id}
                    className={`s27-interest-chip${
                      form.interestType === t.id ? " selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="interestType"
                      value={t.id}
                      checked={form.interestType === t.id}
                      onChange={() =>
                        setForm((prev) => ({ ...prev, interestType: t.id }))
                      }
                    />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="summit-field">
            <label className="summit-field-label" htmlFor="summit-name">
              Name
            </label>
            <input
              id="summit-name"
              className={`summit-input${showError("name") ? " has-error" : ""}`}
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={setField("name")}
              onBlur={markTouched("name")}
              placeholder="Your full name"
            />
            {showError("name") && (
              <div className="summit-field-error">{errors.name}</div>
            )}
          </div>

          <div className="summit-field">
            <label className="summit-field-label" htmlFor="summit-email">
              Email address
            </label>
            <input
              id="summit-email"
              className={`summit-input${showError("email") ? " has-error" : ""}`}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={form.email}
              onChange={setField("email")}
              onBlur={markTouched("email")}
              placeholder="you@example.com"
            />
            {showError("email") && (
              <div className="summit-field-error">{errors.email}</div>
            )}
          </div>

          <div
            className={`summit-field summit-field-property${
              isHost && !isInterest ? " visible" : ""
            }`}
            aria-hidden={!isHost || isInterest}
          >
            <label className="summit-field-label" htmlFor="summit-property">
              Property name
            </label>
            <input
              id="summit-property"
              className={`summit-input${
                showError("propertyName") ? " has-error" : ""
              }`}
              type="text"
              value={form.propertyName}
              onChange={setField("propertyName")}
              onBlur={markTouched("propertyName")}
              placeholder="e.g. Chestnut Hill Cabin"
              tabIndex={isHost ? 0 : -1}
            />
            {showError("propertyName") && (
              <div className="summit-field-error">{errors.propertyName}</div>
            )}
          </div>

          <div className="summit-field">
            <label className="summit-field-label" htmlFor="summit-social">
              Social media handle{" "}
              <span className="summit-field-optional">(optional)</span>
            </label>
            <div className="summit-input-handle">
              <span className="summit-input-handle-prefix" aria-hidden="true">
                @
              </span>
              <input
                id="summit-social"
                className="summit-input"
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                value={form.socialHandle}
                onChange={setField("socialHandle")}
                placeholder="yourhandle"
              />
            </div>
          </div>

          {isInterest && (
            <div className="summit-field">
              <label className="summit-field-label" htmlFor="summit-topic">
                {topicLabelFor(form.interestType)}
              </label>
              <textarea
                id="summit-topic"
                className={`summit-input s27-textarea${
                  showError("topic") ? " has-error" : ""
                }`}
                rows={4}
                maxLength={TOPIC_MAX}
                value={form.topic}
                onChange={setField("topic")}
                onBlur={markTouched("topic")}
                placeholder={topicPlaceholderFor(form.interestType)}
              />
              <div className="s27-char-count">
                {form.topic.length}/{TOPIC_MAX}
              </div>
              {showError("topic") && (
                <div className="summit-field-error">{errors.topic}</div>
              )}
            </div>
          )}

          {isInterest && (
            <>
              {submitError && (
                <div className="summit-notice error">{submitError}</div>
              )}
              <button
                className="summit-btn primary summit-ticket-submit"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Send my interest — it's free"}
              </button>
              <div className="summit-trust-row">
                <span className="summit-trust-item">🎤 No ticket or payment required</span>
                <span className="summit-trust-item">✉️ Jamie replies personally</span>
              </div>
            </>
          )}

          <div
            className={`s27-speaker-box${form.speakerInterest ? " open" : ""}${
              isInterest ? " s27-hidden" : ""
            }`}
            aria-hidden={isInterest}
          >
            <label className="s27-speaker-toggle" htmlFor="summit-speaker">
              <input
                id="summit-speaker"
                type="checkbox"
                className="s27-speaker-checkbox"
                checked={form.speakerInterest}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    speakerInterest: e.target.checked,
                  }))
                }
              />
              <span className="s27-speaker-toggle-text">
                <span className="s27-speaker-toggle-title">
                  I'm interested in being a speaker at the 2027 Summit
                </span>
                <span className="s27-speaker-toggle-sub">
                  Optional — we'll reach out as we build the lineup.
                </span>
              </span>
            </label>
            <div
              className="s27-speaker-topic"
              aria-hidden={!form.speakerInterest}
            >
              <label
                className="summit-field-label"
                htmlFor="summit-speaker-topic"
              >
                What would you like to speak about?{" "}
                <span className="summit-field-optional">(optional)</span>
              </label>
              <textarea
                id="summit-speaker-topic"
                className="summit-input s27-textarea"
                rows={3}
                maxLength={SPEAKER_TOPIC_MAX}
                value={form.speakerTopic}
                onChange={setField("speakerTopic")}
                placeholder="A topic, a story, or an area of expertise you'd love to share…"
                tabIndex={form.speakerInterest ? 0 : -1}
              />
              <div className="s27-char-count">
                {form.speakerTopic.length}/{SPEAKER_TOPIC_MAX}
              </div>
            </div>
          </div>

          {isInterest ? null : couponOpen || form.couponCode ? (
            <div className="summit-field">
              <label className="summit-field-label" htmlFor="summit-coupon">
                Coupon code{" "}
                <span className="summit-field-optional">(optional)</span>
              </label>
              <input
                id="summit-coupon"
                className={`summit-input summit-input-coupon${
                  couponError ? " has-error" : ""
                }`}
                type="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                value={form.couponCode}
                onChange={(e) => {
                  setCouponError("");
                  const value = e.target.value;
                  setForm((prev) => ({ ...prev, couponCode: value }));
                }}
                placeholder="Enter code"
              />
              {couponError && (
                <div className="summit-field-error">{couponError}</div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="summit-coupon-toggle"
              onClick={() => setCouponOpen(true)}
            >
              Have a coupon code?
            </button>
          )}

          {!isInterest && (
            <>
              {submitError && (
                <div className="summit-notice error">{submitError}</div>
              )}

              <button
                className="summit-btn primary summit-ticket-submit"
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  "Preparing secure checkout…"
                ) : (
                  <>
                    Continue to payment —{" "}
                    <EarlyBirdPrice size="button" />
                  </>
                )}
              </button>
              <div className="s27-refund-note">
                <span aria-hidden="true">↩︎</span> {REFUND_NOTE}.
              </div>
              <div className="summit-trust-row">
                <span className="summit-trust-item">🔒 Secure checkout powered by Stripe</span>
                <span className="summit-trust-item">✉️ Instant email confirmation</span>
              </div>
            </>
          )}
        </form>
      )}
    </div>
  );
};

const Summit2027 = () => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const originalTitle = document.title;
    const originals = SUMMIT_META_KEYS.map((tag) => {
      const el = getMetaEl(tag);
      return { tag, el, value: el ? el.getAttribute("content") : null };
    });

    document.title = SUMMIT_TITLE;
    originals.forEach(({ tag, el }) => {
      if (!el) return;
      const isTitleTag = tag.key.endsWith("title");
      el.setAttribute("content", isTitleTag ? SUMMIT_TITLE : SUMMIT_DESCRIPTION);
    });

    return () => {
      document.title = originalTitle;
      originals.forEach(({ el, value }) => {
        if (!el) return;
        if (value == null) {
          el.removeAttribute("content");
        } else {
          el.setAttribute("content", value);
        }
      });
    };
  }, []);

  const body = (
    <div className="summit-page">
      <div className="summit-intro">
        <img
          className="summit-hero-image s27-hero-image"
          src="/summit-2027-cabin.jpg"
          alt="Guest wrapped in a blanket on the porch of a forest cabin"
        />
        <div className="s27-badge">
          <span className="s27-badge-dot" aria-hidden="true" />
          Early Access Pricing · Save {EARLY_BIRD_SAVINGS_LABEL}
        </div>
        <h1 className="summit-hero-title">Postaway Hospitality Summit 2027</h1>
        <div className="summit-subtitle">STR Hosts / Creators</div>
        <div className="summit-event-meta">
          <span>{EVENT_DATES}</span>
          <span className="summit-meta-dot" aria-hidden="true">·</span>
          <span>{EVENT_LOCATION}</span>
        </div>
        <a
          className="summit-btn primary summit-hero-cta s27-hero-cta"
          href="#tickets"
          onClick={scrollToTickets}
        >
          Get Early Access Tickets · <EarlyBirdPrice size="button" />
        </a>
        <div className="s27-hero-refund">
          <span aria-hidden="true">↩︎</span> {REFUND_NOTE}
        </div>
        <a className="s27-hero-interest-link" href="#speak" onClick={jumpToInterest}>
          Interested in speaking or sponsoring? Skip the ticket →
        </a>

        <div className="s27-early-bird">
          <div className="s27-early-bird-eyebrow">Early Access Ticket</div>
          <div className="s27-early-bird-pricing">
            <EarlyBirdPrice size="hero" />
            <span className="s27-early-bird-per">per attendee · all 3 days</span>
          </div>
          <ul className="s27-early-bird-perks">
            <li>
              <strong>{EARLY_BIRD_SAVINGS_LABEL} off</strong> the regular {REGULAR_PRICE_LABEL} ticket
              price
            </li>
            <li>
              <strong>{REFUND_NOTE}</strong> — no questions asked
            </li>
            <li>
              Lock in your spot for {EVENT_DATES} in {EVENT_LOCATION}
            </li>
          </ul>
        </div>

        <div className="summit-schedule">
          <div className="summit-schedule-eyebrow">What to Expect</div>
          <p className="summit-schedule-intro">
            Back in Breckenridge for year two. Three days of track-specific
            learning, shared sessions, and time to connect with hosts and
            creators who get it.
          </p>
          <div className="summit-session-types">
            {HIGHLIGHTS.map((item) => (
              <div key={item.label} className="summit-session-type">
                <div className="summit-session-type-label">{item.label}</div>
                <div className="summit-session-type-text">{item.text}</div>
              </div>
            ))}
          </div>
          <p className="summit-schedule-disclaimer">
            Speakers, venue, and the full schedule will be announced as they're
            finalized. Early access pricing is available for a limited time.
          </p>
        </div>

        <p className="summit-description">
          Join hospitality hosts and creators in the Rockies for three days of
          connection, learning, and collaboration. Spaces are limited — lock in
          early access pricing below and we'll see you in September 2027.
        </p>
        <Summit2027Tickets />
      </div>
      <SummitApplicationStyles />
      <Summit2027Styles />
    </div>
  );

  return <InfoPage below={body} disableStamps />;
};

const Summit2027Styles = () => (
  <style>{`
    /* ---- Price (strikethrough regular → early bird) ---- */
    .s27-price {
      display: inline-flex;
      align-items: baseline;
      gap: 8px;
      white-space: nowrap;
    }
    .s27-price-was {
      color: #777e90;
      font-weight: 500;
      text-decoration: line-through;
      text-decoration-thickness: 2px;
    }
    .s27-price-now {
      font-weight: 700;
    }
    .s27-price-button .s27-price-was {
      color: rgba(255, 255, 255, 0.72);
      font-size: 0.9em;
    }
    .s27-price-button .s27-price-now { color: #fff; }
    .s27-price-card { gap: 8px; }
    .s27-price-card .s27-price-was { font-size: 15px; }
    .s27-price-card .s27-price-now { font-size: 24px; color: #dc1d08; line-height: 1.2; }
    .s27-price-hero { gap: 12px; }
    .s27-price-hero .s27-price-was { font-size: 22px; }
    .s27-price-hero .s27-price-now {
      font-family: 'Sailors', serif;
      font-size: 48px;
      color: #dc1d08;
      line-height: 1;
    }
    @media (max-width: 480px) {
      .s27-price-hero .s27-price-was { font-size: 18px; }
      .s27-price-hero .s27-price-now { font-size: 40px; }
    }

    /* Portrait source photo — a 21:9 banner would crop it to a sliver. */
    .summit-hero-image.s27-hero-image {
      aspect-ratio: 16 / 10;
      object-position: 50% 56%;
    }
    @media (max-width: 768px) {
      .summit-hero-image.s27-hero-image {
        aspect-ratio: 4 / 3;
        object-position: 50% 62%;
      }
    }

    /* ---- Hero badge + refund line ---- */
    .s27-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #dc1d08;
      background: #fdecea;
      border-radius: 100px;
      padding: 7px 14px;
      margin-bottom: 2px;
    }
    @media (max-width: 480px) {
      .s27-badge { font-size: 11px; letter-spacing: 0.12em; padding: 6px 12px; text-align: center; }
    }
    .s27-badge-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #dc1d08;
      flex: none;
    }
    .s27-hero-cta {
      gap: 6px;
      flex-wrap: wrap;
    }
    .s27-hero-refund,
    .s27-refund-note {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #0f084b;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      text-align: center;
    }
    .s27-hero-refund { margin-top: -2px; }
    .s27-refund-note {
      width: 100%;
      justify-content: center;
      margin-top: -6px;
      color: #353945;
      font-weight: 500;
    }

    /* ---- Early bird callout ---- */
    .s27-early-bird {
      width: 100%;
      max-width: 640px;
      box-sizing: border-box;
      background: #0f084b;
      color: #fff;
      border-radius: 16px;
      padding: 26px 28px;
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      text-align: center;
    }
    @media (max-width: 768px) {
      .s27-early-bird { padding: 22px 18px; border-radius: 14px; }
    }
    .s27-early-bird-eyebrow {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      opacity: 0.85;
    }
    .s27-early-bird-pricing {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }
    .s27-early-bird .s27-price-was { color: rgba(255, 255, 255, 0.6); }
    .s27-early-bird .s27-price-now { color: #fff; }
    .s27-early-bird-per {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.04em;
      opacity: 0.85;
    }
    .s27-early-bird-perks {
      list-style: none;
      margin: 4px 0 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 8px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      line-height: 1.5;
    }
    .s27-early-bird-perks li {
      position: relative;
      padding-left: 22px;
      text-align: left;
    }
    .s27-early-bird-perks li::before {
      content: '✓';
      position: absolute;
      left: 0;
      top: 0;
      color: #ff8a7a;
      font-weight: 700;
    }
    .s27-early-bird-perks strong { color: #fff; }

    /* ---- Hero: speaker/sponsor shortcut ---- */
    .s27-hero-interest-link {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #dc1d08;
      text-decoration: none;
      margin-top: 2px;
      text-align: center;
    }
    .s27-hero-interest-link:hover { text-decoration: underline; }

    /* ---- Ticket cards ---- */
    .s27-ticket-cards { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    @media (max-width: 700px) {
      .s27-ticket-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .s27-ticket-cards .s27-interest-card { grid-column: 1 / -1; }
    }
    @media (max-width: 480px) {
      .s27-ticket-cards { grid-template-columns: 1fr; }
      .s27-ticket-cards .s27-interest-card { grid-column: auto; }
    }
    .s27-ticket-card { padding-top: 34px; }
    .s27-interest-card { background: #fff7f5; }
    .s27-interest-card .summit-ticket-card-desc { text-align: left; }
    .s27-interest-card.selected { border-color: #dc1d08; background: #ffffff; }
    .s27-interest-card .summit-ticket-card-check { background: #dc1d08; }
    .s27-ticket-card-tag-interest { color: #0f084b; background: #e9e7f7; }
    .s27-interest-card-price {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 18px;
      font-weight: 700;
      color: #0f084b;
      line-height: 1.4;
      margin-top: 2px;
    }

    /* ---- Interest form ---- */
    .s27-hidden { display: none !important; }
    .s27-interest-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .s27-interest-chip {
      position: relative;
      display: inline-flex;
      align-items: center;
      padding: 9px 16px;
      border-radius: 100px;
      border: 1.5px solid #c9c5e8;
      background: #fff;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: #0f084b;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s, color 0.15s;
      user-select: none;
    }
    .s27-interest-chip input {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      pointer-events: none;
    }
    .s27-interest-chip:hover { border-color: #0f084b; }
    .s27-interest-chip.selected {
      background: #0f084b;
      border-color: #0f084b;
      color: #fff;
    }
    .s27-interest-chip:has(input:focus-visible) {
      outline: 2px solid #dc1d08;
      outline-offset: 2px;
    }
    @media (max-width: 480px) {
      .s27-interest-chip { flex: 1 1 auto; justify-content: center; }
    }
    .s27-interest-sent .summit-confirmed-title { color: #0f084b; }
    .s27-interest-ticket-cta { margin-top: 14px; }
    .s27-ticket-card-tag {
      position: absolute;
      top: 10px;
      left: 12px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: #dc1d08;
      background: #fdecea;
      border-radius: 100px;
      padding: 3px 8px;
      line-height: 1.3;
    }
    .s27-ticket-card .summit-ticket-card-price { margin-top: 2px; }

    /* ---- Speaker interest box ---- */
    .s27-speaker-box {
      width: 100%;
      box-sizing: border-box;
      background: #f4f3fb;
      border: 1.5px solid transparent;
      border-radius: 14px;
      padding: 14px 16px;
      text-align: left;
      transition: border-color 0.15s, background 0.15s;
    }
    .s27-speaker-box.open {
      background: #ffffff;
      border-color: #0f084b;
    }
    .s27-speaker-toggle {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      cursor: pointer;
    }
    .s27-speaker-checkbox {
      appearance: none;
      -webkit-appearance: none;
      flex: none;
      width: 22px;
      height: 22px;
      margin: 1px 0 0;
      border: 2px solid #c9c5e8;
      border-radius: 6px;
      background: #fff;
      cursor: pointer;
      display: inline-grid;
      place-content: center;
      transition: border-color 0.15s, background 0.15s;
    }
    .s27-speaker-checkbox::before {
      content: '';
      width: 12px;
      height: 12px;
      transform: scale(0);
      transition: transform 0.12s ease-in-out;
      background: #fff;
      clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    }
    .s27-speaker-checkbox:checked {
      background: #0f084b;
      border-color: #0f084b;
    }
    .s27-speaker-checkbox:checked::before { transform: scale(1); }
    .s27-speaker-checkbox:focus-visible {
      outline: 2px solid #dc1d08;
      outline-offset: 2px;
    }
    .s27-speaker-toggle-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
    }
    .s27-speaker-toggle-title {
      font-size: 14px;
      font-weight: 700;
      color: #0f084b;
      line-height: 1.35;
    }
    .s27-speaker-toggle-sub {
      font-size: 12px;
      color: #777e90;
      line-height: 1.4;
    }
    .s27-speaker-topic {
      display: flex;
      flex-direction: column;
      gap: 6px;
      max-height: 0;
      opacity: 0;
      overflow: hidden;
      transition: max-height 0.25s ease, opacity 0.25s ease, margin-top 0.25s ease;
      margin-top: 0;
    }
    .s27-speaker-box.open .s27-speaker-topic {
      max-height: 260px;
      opacity: 1;
      margin-top: 14px;
    }
    .s27-textarea {
      resize: vertical;
      min-height: 88px;
      line-height: 1.5;
    }
    .s27-char-count {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      color: #a5a1bd;
      text-align: right;
    }
  `}</style>
);

export default Summit2027;
